import React, { useState, useEffect } from 'react'
import { ChatMessage as ChatMessageType } from '../../redux/states/chatbot'
import ReactMarkdown from 'react-markdown'
import { useSelector } from 'react-redux'
import { AppState } from '../../redux/states/appState'
import { LuxChatbotIcon } from '@lux/wardrobe-lib'

interface ChatMessageProps {
	message: ChatMessageType
	isLastMessage: boolean
}

const CachedImage = React.memo(
	({ src, alt }: { src: string; alt?: string }) => {
		return <img src={src} alt={alt} style={{ maxWidth: '100%' }} />
	},
	(prevProps, nextProps) => prevProps.src === nextProps.src
)

function areEqual(prevProps: ChatMessageProps, nextProps: ChatMessageProps) {
	return (
		prevProps.message.text === nextProps.message.text &&
		prevProps.message.isTyping === nextProps.message.isTyping &&
		prevProps.isLastMessage === nextProps.isLastMessage
	)
}

const ChatMessageComponent: React.FC<ChatMessageProps> = ({
	message,
	isLastMessage,
}) => {
	const user = useSelector((state: AppState) => state.userReducer.user)
	const isUser = message.role === 'user'
	const [displayText, setDisplayText] = useState('')
	const [isStreamingFinished, setIsStreamingFinished] = useState(false)

	useEffect(() => {
		if (isUser) {
			setDisplayText(message.text)
			setIsStreamingFinished(true)
		} else if (message.isTyping && isLastMessage) {
			const words = message.text.split(' ')
			let currentIndex = 0
			setDisplayText('')
			setIsStreamingFinished(false)

			const interval = setInterval(() => {
				if (currentIndex < words.length) {
					setDisplayText((prev) => prev + words[currentIndex] + ' ')
					currentIndex++
				} else {
					clearInterval(interval)
					setIsStreamingFinished(true)
				}
			}, 100)

			return () => clearInterval(interval)
		} else {
			setDisplayText(message.text)
			setIsStreamingFinished(true)
		}
	}, [message.text, message.isTyping, isLastMessage, isUser])

	const getUserInitials = () => {
		if (!user) return 'U'
		const givenName = user.given_name || ''
		const familyName = user.family_name || ''
		return `${givenName.charAt(0)}${familyName.charAt(0)}`.toUpperCase()
	}

	return (
		<div
			className={`chat-message ${
				isUser ? 'chat-message-user' : 'chat-message-ai'
			}`}>
			<div
				className={`chat-avatar ${
					isUser ? 'chat-avatar-user' : 'chat-avatar-ai'
				}`}>
				{isUser ? (
					getUserInitials()
				) : (
					<LuxChatbotIcon size={16} color="currentColor" />
				)}
			</div>
			<div className="chat-message-content">
				<div
					style={{
						wordBreak: 'break-word',
						fontFamily: 'inherit',
					}}>
					<ReactMarkdown
						components={{
							img: ({ node, ...props }) =>
								isStreamingFinished ? (
									<CachedImage
										src={props.src}
										alt={props.alt}
									/>
								) : null,
						}}>
						{displayText}
					</ReactMarkdown>
				</div>
				{message.isTyping && isLastMessage && !isUser && (
					<span className="typing-indicator">Sta scrivendo...</span>
				)}
			</div>
		</div>
	)
}

export const ChatMessage = React.memo(ChatMessageComponent, areEqual)
